import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';

import { Select, Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, first, takeUntil } from 'rxjs/operators';

import { AUTH_TOKEN } from '@recruitee/common';
import { TutorialDialogService } from '@recruitee/components/tutorial-dialog';
import {
  MarkFeatureAsDiscovered,
  ReferralsPortalProfile,
  ReferralsPortalProfileRepo,
} from '@recruitee/http';
import { RtPlatform, WindowRef } from '@recruitee/user-interface';

import { ScrollService } from '../services/scroll.service';
import { LogOut, LogOutWithRecruitee } from '../state/core.actions';
import { SetHeaderMenuContext, ToggleHeader } from '../state/main-view/main-view.actions';
import {
  HeaderMenuContext,
  IMainViewState,
  MainViewState,
} from '../state/main-view/main-view.state';
import { StickyFooterService } from './footer/sticky/sticky-footer.service';

@Component({
  selector: 'rtr-main-view',
  templateUrl: './main-view.component.html',
  styleUrls: ['./main-view.component.less'],
})
export class MainViewComponent implements OnInit, AfterViewInit, OnDestroy {
  public headerHidden: boolean = false;
  public sidebarOpen: boolean = false;
  public hasBreadcrumbs: boolean = false;
  public templateRef: TemplateRef<any> | null;

  @ViewChild('header', { static: true }) public header: ElementRef;

  @Select(MainViewState)
  public mainViewState$: Observable<IMainViewState>;

  @Select(ReferralsPortalProfileRepo.activeEntity)
  public profile$: Observable<ReferralsPortalProfile>;

  private ngOnDestroy$: Subject<void> = new Subject();
  private isAdmin: boolean;

  constructor(
    @Inject(AUTH_TOKEN) private token: string,
    private store: Store,
    private cdRef: ChangeDetectorRef,
    private stickyFooterService: StickyFooterService,
    private scrollService: ScrollService,
    private rtPlatform: RtPlatform,
    private winRef: WindowRef,
    private tutorialDialogService: TutorialDialogService,
    private vcRef: ViewContainerRef,
  ) {}

  public ngOnInit(): void {
    this.mainViewState$
      .pipe(distinctUntilChanged(), takeUntil(this.ngOnDestroy$))
      .subscribe(({ sidebarOpen, headerHidden, breadcrumbs }) => {
        this.sidebarOpen = sidebarOpen;
        this.headerHidden = headerHidden;
        this.hasBreadcrumbs = breadcrumbs && !!breadcrumbs.length;
        this.cdRef.detectChanges();
      });

    this.stickyFooterService.template$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(templateRef => {
      this.templateRef = templateRef;
      this.cdRef.detectChanges();
    });

    this.store.dispatch(
      new SetHeaderMenuContext(this.token ? HeaderMenuContext.Logged : HeaderMenuContext.NotLogged),
    );

    this.profile$.pipe(filter(Boolean), first()).subscribe((profile: ReferralsPortalProfile) => {
      const { isAdmin, referralIntroUsed } = profile;
      this.isAdmin = isAdmin;

      if (!referralIntroUsed)
        this.tutorialDialogService
          .open('referrals_onboarding', this.vcRef)
          .afterClosed.pipe(takeUntil(this.ngOnDestroy$.asObservable()))
          .subscribe(_ => {
            this.store.dispatch(new MarkFeatureAsDiscovered({ referralIntroUsed: true }));
          });
    });

    this.store
      .select(MainViewState.isMainHeaderHidden)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(headerHidden => (this.headerHidden = headerHidden));
  }

  public logOut(): void {
    this.isAdmin ? this.logOutRecruiteeUser() : this.logOutNoneRecruiteeUser();
  }

  public ngAfterViewInit(): void {
    if (this.rtPlatform.IS_MOBILE) {
      this.scrollService.scrollingDown$
        .pipe(
          filter(
            _ =>
              !(
                window.innerHeight + window.scrollY >=
                document.body.offsetHeight - this.header.nativeElement.clientHeight
              ),
          ),
          distinctUntilChanged(),
          takeUntil(this.ngOnDestroy$),
        )
        .subscribe(scrollingDown => {
          const { offsetTop, clientHeight } = this.header.nativeElement;
          this.store.dispatch(new ToggleHeader(scrollingDown && offsetTop > clientHeight));
        });
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private logOutRecruiteeUser(): void {
    this.store.dispatch(new LogOutWithRecruitee());
  }

  private logOutNoneRecruiteeUser(): void {
    this.store
      .dispatch(new LogOut())
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe({
        next: () => {
          this.winRef.nativeWindow.location.replace('/');
        },
      });
  }
}
